import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { getWsUrl } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

const RealtimeContext = createContext(null);
export const useRealtime = () => useContext(RealtimeContext);

// Backoff progressivo per evitare "storm" di riconnessioni (causa possibile di freeze UI):
// 2s, 4s, 8s, 16s, fino a max 30s. Il contatore si azzera ad ogni connessione riuscita.
const BASE_BACKOFF_MS = 2000;
const MAX_BACKOFF_MS = 30000;
const PING_MS = 25000;

export function RealtimeProvider({ children }) {
  const { token, user } = useAuth();
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const listenersRef = useRef(new Set());
  const reconnectRef = useRef(null);
  const attemptsRef = useRef(0);
  const stoppedRef = useRef(false);

  const subscribe = useCallback((fn) => {
    listenersRef.current.add(fn);
    return () => listenersRef.current.delete(fn);
  }, []);

  useEffect(() => {
    // Ad ogni cambio token/utente ripartiamo puliti.
    stoppedRef.current = false;
    attemptsRef.current = 0;

    const clearReconnect = () => {
      if (reconnectRef.current) {
        clearTimeout(reconnectRef.current);
        reconnectRef.current = null;
      }
    };

    const scheduleReconnect = () => {
      if (stoppedRef.current) return;
      clearReconnect();
      const n = attemptsRef.current;
      attemptsRef.current = n + 1;
      const delay = Math.min(BASE_BACKOFF_MS * 2 ** n, MAX_BACKOFF_MS);
      reconnectRef.current = setTimeout(connect, delay);
    };

    const connect = () => {
      if (stoppedRef.current) return;
      const existing = wsRef.current;
      // Non aprire un nuovo socket se ne esiste già uno in connessione (0) o aperto (1).
      if (existing && (existing.readyState === 0 || existing.readyState === 1)) return;

      let ws;
      try {
        ws = new WebSocket(getWsUrl(token));
      } catch (e) {
        scheduleReconnect();
        return;
      }
      wsRef.current = ws;

      ws.onopen = () => {
        attemptsRef.current = 0; // reset backoff su connessione riuscita
        setConnected(true);
      };
      ws.onclose = () => {
        setConnected(false);
        if (!stoppedRef.current) scheduleReconnect();
      };
      ws.onerror = () => { try { ws.close(); } catch (e) { /* noop */ } };
      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          listenersRef.current.forEach((fn) => fn(msg));
        } catch (e) { /* ignora messaggi non-json (es. pong) */ }
      };
    };

    connect();

    // Heartbeat: invia ping SOLO se il socket è realmente aperto.
    const ping = setInterval(() => {
      const ws = wsRef.current;
      if (ws && ws.readyState === 1) {
        try { ws.send('ping'); } catch (e) { /* noop */ }
      }
    }, PING_MS);

    // Quando la tab torna visibile o la rete torna online, riconnetti subito (backoff azzerato).
    const wakeUp = () => {
      if (stoppedRef.current) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      const ws = wsRef.current;
      if (!ws || ws.readyState === 2 || ws.readyState === 3) {
        attemptsRef.current = 0;
        clearReconnect();
        connect();
      }
    };
    document.addEventListener('visibilitychange', wakeUp);
    window.addEventListener('online', wakeUp);
    window.addEventListener('focus', wakeUp);

    return () => {
      // Cleanup completo: nessuna riconnessione fantasma dopo unmount / cambio token.
      stoppedRef.current = true;
      clearReconnect();
      clearInterval(ping);
      document.removeEventListener('visibilitychange', wakeUp);
      window.removeEventListener('online', wakeUp);
      window.removeEventListener('focus', wakeUp);
      const ws = wsRef.current;
      if (ws) {
        ws.onopen = null;
        ws.onclose = null;
        ws.onerror = null;
        ws.onmessage = null;
        try { ws.close(); } catch (e) { /* noop */ }
      }
      wsRef.current = null;
    };
    // Riconnessione quando cambia il token (login/logout) o l'utente.
  }, [token, user]);

  return (
    <RealtimeContext.Provider value={{ connected, subscribe }}>
      {children}
    </RealtimeContext.Provider>
  );
}
